require('./lineChartProfitability.scss');

const PropTypes = require('prop-types');

const React= require('react');
const moment = require('moment');
const d3 = require('d3');
const isEqual = require('lodash/lang/isEqual');
const reduce = require('lodash/collection/reduce');
const {xTicksAmount, yTicksAmount, decideAxisDateLabel, createTooltip, yAxisFormatLabel} = require('./lineChartProfitabilityHelpers.js');

const blackBox = props => {

    const {dataset, width, height, chartType, currencyId} = props;

    return me => {
        const xaxisG = me.select('.line-chart-profitability__xaxis');
        const yaxisG = me.select('.line-chart-profitability__yaxis');
        const gridG = me.select('.line-chart-profitability__grid');
        const circlesG = me.select('.line-chart-profitability__circles');
        const lineG = me.select('.line-chart-profitability__line');
        const areaG = me.select('.line-chart-profitability__area');
        const tooltip = me.select('.line-chart-profitability__tooltip');
        const tooltipContent = me.select('.line-chart-profitability__tooltip-content');
        const dataArray = dataset.toArray();

        const maxDate = d3.max(dataArray, d => moment.utc(d.get('Date')));
        const minDate = d3.min(dataArray, d => moment.utc(d.get('Date')));
        const maxVal = d3.max(dataArray, d => +d.get('Value'));
        const minVal = d3.min(dataArray, d => +d.get('Value'));
        const xDomain = [ minDate, maxDate ];
        const yDomain = [ minVal, maxVal ];
        const xAxisFormat = decideAxisDateLabel(maxDate, minDate);
        const yAxisFormat = (d) => yAxisFormatLabel(d, currencyId);
        const xAxisTicksAmount = xTicksAmount(chartType);
        const yAxisTicksAmount = yTicksAmount(chartType);

        const padding = 60;

        const yScale = d3.scale.linear()
            .domain(yDomain)
            .range([height - padding, padding]);

        const xScale = d3.time.scale()
            .domain(xDomain)
            .range([padding * 2, width - padding]);

        const yAxis = d3.svg.axis()
            .orient('right')
            .scale(yScale)
            .tickSize(0)
            .ticks(yAxisTicksAmount)
            .outerTickSize(0)
            .tickFormat(yAxisFormat);

        const xAxis = d3.svg.axis()
            .orient('bottom')
            .scale(xScale)
            .tickSize(0)
            .ticks(xAxisTicksAmount)
            .outerTickSize(0)
            .tickFormat(xAxisFormat);

        const lineArray = dataArray.map(item => {
            return [
                xScale(moment.utc(item.get('Date'))),
                yScale(item.get('Value'))
            ];
        });

        const drawLine = d3.svg.line()
                .interpolate('linear');

        const prepareTransition = d3.svg.line()
                .y(() => { return height - padding; })
                .interpolate('linear');

        const x = dateString => xScale(moment.utc(dateString));

        const y = amount => yScale(amount);

        let circle = circlesG.selectAll('circle')
                .data(dataArray);

        circle.exit().remove();
        gridG.selectAll('line').remove();
        areaG.selectAll('path').remove();
        lineG.selectAll('path').remove();

        let line = lineG.append('path')
                .datum(lineArray);

        line.attr('class', 'line')
            .attr('d', prepareTransition)
            .transition().duration(1000)
            .attr('d', drawLine);

        circle.enter().append('circle');

        circle.attr('r', 1e-6)
            .attr('cx', d => x(d.get('Date')))
            .attr('cy', d => y(d.get('Value')))
            .style('opacity', 0)
            .on('mouseover', function (d) {
                d3.select(this).style('opacity', 1);
                gridG.append('svg:line')
                    .classed('line-chart-profitability__vertical-grid', true)
                    .attr({
                        x1: x(d.get('Date')),
                        x2: x(d.get('Date')),
                        y1: y(yDomain[0]),
                        y2: y(d.get('Value')),
                        'stroke': ("2, 3")
                    });

                tooltipContent.html(createTooltip(d, currencyId));

                tooltip.style('left', () => {
                        return `${x(d.get('Date'))}px`;
                    })
                    .style('top', () => {
                        return `${y(d.get('Value'))}px`;
                    })
                    .style('display', 'block');
            })
            .on('mouseleave', function () {
                d3.select(this).style('opacity', 0);
                tooltip.style('display', 'none');
                d3.selectAll('.line-chart-profitability__vertical-grid').remove();
            })
            .transition().duration(750).ease('elastic')
                .attr('r', 6.5);

        xScale.ticks(xAxisTicksAmount).forEach(tick => {
            gridG.append('svg:line')
              .attr({
                x1: x(tick),
                x2: x(tick),
                y1: y(yDomain[0]),
                y2: y(yDomain[1]),
                'stroke-dasharray': ("2, 3")
              })
              .classed('line-chart-profitability__horizontal-grid', true);
        });

        yScale.ticks(yAxisTicksAmount).forEach(tick => {
            gridG.append('svg:line')
              .attr({
                x1: x(xDomain[0]),
                x2: x(xDomain[1]),
                y1: y(tick),
                y2: y(tick),
                'stroke-dasharray': ("2, 3")
              })
              .classed('line-chart-profitability__horizontal-grid', true);
        });

        yaxisG.attr('class', 'line-chart-profitability__yaxis line-chart-profitability__axis')
            .attr('transform', 'translate('+padding*2+',0)')
            .call(yAxis);

        xaxisG.attr('class', 'line-chart-profitability__xaxis line-chart-profitability__axis')
            .attr('transform', 'translate(0,' + (height - padding) + ')')
            .call(xAxis);

        xaxisG.selectAll('.line-chart-profitability__xaxis text')
            .attr('transform', 'translate(0, 10)');

        yaxisG.selectAll('.line-chart-profitability__yaxis text')
            .attr('transform', 'translate(-60, 0)');
    }
}

class LineChart extends React.Component {

    constructor(props) {
        super(props);
        this.setNode = this.setNode.bind(this);
    }

    componentDidMount() {
        if (this.props.dataset.size > 0) {
            d3.select(this.nodeReference)
              .call(blackBox(this.props));
        }
    }

    componentDidUpdate(old) {
        let difference = reduce(this.props, (result, value, key) => {
            return isEqual(value, old[key]) ? result : result.concat(key);
        }, []);

        if (difference.length === 0) { return; }

        d3.select(this.nodeReference)
            .call(blackBox(this.props));
    }

    setNode(ref) {
        this.nodeReference = ref;
    }

    render() {
        return (
            <div ref={this.setNode} className="line-chart-profitability">

                <svg
                    width={this.props.width}
                    height={this.props.height}
                    shapeRendering="geometricPrecision"
                >
                    <g className="line-chart-profitability__grid"></g>
                    <g className="line-chart-profitability__xaxis"></g>
                    <g className="line-chart-profitability__yaxis"></g>
                    <g className="line-chart-profitability__line">
                        <path></path>
                    </g>
                    <g className="line-chart-profitability__circles"></g>
                </svg>
                <div className="line-chart-profitability__tooltip">
                    <div className="line-chart-profitability__tooltip-content">
                    </div>
                </div>
                <div className="line-chart-profitability__top-text"></div>
            </div>
        );
    }
}

LineChart.propTypes = {
    dataset: PropTypes.object.isRequired,
    width: PropTypes.string.isRequired,
    height: PropTypes.string.isRequired,
    chartType: PropTypes.string.isRequired,
    currencyId: PropTypes.string.isRequired
};

module.exports = LineChart;
